import * as React from "react";
import { motion } from "framer-motion";
import { useMemo } from "react";
import { useDiscussHistory } from "@/hooks/useDiscussHistory";
import { useSendMessage } from "@/hooks/useSendMessage";
import { useDocument } from "@/hooks/useDocument";
import { useUser } from "@/hooks/useUser";
import { CompassSocketProvider, useCompassSocketContext } from "@/context/CompassSocketContext";
import { ChatQueryRequest } from "@/types/discuss.types";
import { Loader } from "lucide-react";
import ChatInputWithGallery from "./ChatInputWithGallery"; // Import the new component
import ReactMarkdown from "react-markdown";
import DiscussBulkActions from "./DiscussBulkActions";

import ScrollToBottomButton from "./ScrollToBottomButton";

const CompassDialog = React.lazy(() => import("./CompassDialog"));
import Message from "./Message";
import MessageLoading from "./MessageLoading";

// We need to update DiscussContent props definition
interface DiscussContentProps {
  showCompassDialog: boolean;
  setShowCompassDialog: (show: boolean) => void;
}

// Update DiscussContent signature
const DiscussContent: React.FC<DiscussContentProps> = ({ showCompassDialog, setShowCompassDialog }) => {
  const lastMessageRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const { data: doc } = useDocument();
  const { user } = useUser();
  const { taskStatuses, activeProcessingTask } = useCompassSocketContext();
  const isCompassProcessing = Object.values(taskStatuses).some(status => status === "processing");

  const welcomeMessage = useMemo(() => {
    return `**Hello ${user?.first_name || "there"}! I'm Amelia,**

I am your all round helper equivalent to a senior associate.

I can review documents, extract information, compare information, do legal research, draft short documents and more.

**Please provide me with explicit and detailed:**

*   Context about the situation.
*   Detailed instructions of exactly what you would like me to do.
*   Anything that you don't want me to do (e.g give you tables or bullet points)`;
  }, [user?.first_name]);

  // Fetch chat history using the hook
  const {
    data: historyData,
    isLoading: isHistoryLoading,
    error: historyError,
  } = useDiscussHistory(doc?._id || "");

  // Mutation hook for sending messages
  const { mutate: sendMessage, isPending: isSendingMessage } = useSendMessage(doc?._id);

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  React.useEffect(() => {
    scrollToBottom();
  }, [historyData?.prev_chat, isSendingMessage, isCompassProcessing]);

  const handleSendMessage = async (message: string) => {
    if (message.trim()) {
      // Prepare API request
      if (!doc?._id || !doc?.title) return;

      const queryRequest: ChatQueryRequest = {
        data: message.trim(),
        document_id: [doc._id],
        document_title: [doc.title],
        current_document_id: doc._id,
        audio: false,
      };

      // Send message using mutation
      return new Promise<void>((resolve, reject) => {
        sendMessage(queryRequest, {
          onSuccess: () => {
            resolve();
          },
          onError: (err) => {
            reject(err);
          }
        });
      });
    }
  };

  const handleCompassClick = () => {
    // Opens the Compass dialog
    setShowCompassDialog(true);
  };

  const handleRelatedQuestionClick = (question: string) => {
    // Hide related questions immediately

    // Prepare API request
    if (!doc?._id || !doc?.title) return;

    const queryRequest: ChatQueryRequest = {
      data: question,
      document_id: [doc._id],
      document_title: [doc.title],
      current_document_id: doc._id,
      audio: false,
    };

    // Send message using mutation
    sendMessage(queryRequest, {
      onSuccess: () => { },
    });
  };



  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Loading state */}
      {isHistoryLoading && (
        <div className="flex justify-center items-center h-[200px]">
          <Loader className="animate-spin" />
          <span className="ml-2">Loading chat history...</span>
        </div>
      )}

      {/* Error state */}
      {historyError && (
        <div className="p-4 text-center text-sm text-danger-200">
          <p>Failed to load chat history. Please try again.</p>
        </div>
      )}

      {/* Chat messages area */}
      {!isHistoryLoading && !historyError && (
        <>
          <div className="flex flex-1 min-h-0 relative">
            <div className="flex-1 overflow-y-auto" ref={scrollContainerRef}>
              <div className="flex flex-col gap-3">
                {historyData?.prev_chat?.length === 0 && !isSendingMessage && (
                  <div className="px-4 py-3 rounded-lg text-sm bg-neutral-700 text-neutral-200">
                    <div className="flex-1 prose prose-sm prose-li:marker:text-neutral-200 max-w-none">
                      <ReactMarkdown
                        components={{
                          a: ({ node, ...props }) => (
                            <a {...props} target="_blank" rel="noopener noreferrer" />
                          ),
                        }}
                      >
                        {welcomeMessage}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
                {historyData?.prev_chat?.map((message, index) => {
                  const isLastItem = index === (historyData?.prev_chat?.length || 0) - 1;
                  const isLastAssistantMessage =
                    isLastItem && !!message.response;

                  return (
                    <React.Fragment key={`chat-${index}`}>
                      {message.question && (
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                          className="w-full"
                          ref={isLastItem && !message.response && !activeProcessingTask ? lastMessageRef : null}
                        >
                          <Message
                            type="user"
                            message={message.question}
                            creation_date={message.creation_date}
                          />
                        </motion.div>
                      )}

                      {message.response && (
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                          className="w-full"
                          ref={isLastItem && !activeProcessingTask ? lastMessageRef : null}
                        >
                          <Message
                            type="assistant"
                            message={message.response}
                            creation_date={message.creation_date}
                            messageObject={message}
                            relatedQuestions={
                              isCompassProcessing ||
                                isSendingMessage ||
                                activeProcessingTask
                                ? []
                                : message.related_questions
                            }
                            onRelatedQuestionClick={(question) => handleRelatedQuestionClick(question)} // Updated
                            requestType="discuss"
                            documentId={doc?._id}
                            documentTitle={doc?.title}
                            prevMessage={historyData?.prev_chat?.[index - 1]}
                            bulkActions={
                              isLastAssistantMessage && !activeProcessingTask ? ( // Hide bulk actions if processing next task
                                <DiscussBulkActions
                                  messages={historyData?.prev_chat || []}
                                  documentId={doc?._id}
                                  documentTitle={doc?.title}
                                />
                              ) : undefined
                            }
                          />
                        </motion.div>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Active Processing Task (Optimistic User Message) */}
                {activeProcessingTask && (() => {
                  const prompt = activeProcessingTask.prompt || "Processing...";
                  // De-dupe: Check if the last message in history is the same as the processing one
                  const lastHistoryMessage = historyData?.prev_chat?.[(historyData.prev_chat?.length || 0) - 1];
                  const isDuplicate = lastHistoryMessage && lastHistoryMessage.question === prompt;

                  if (isDuplicate) return null;

                  return (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="w-full"
                      ref={lastMessageRef}
                    >
                      <Message
                        type="user"
                        message={prompt}
                        creation_date={new Date().toISOString()}
                      />
                    </motion.div>
                  );
                })()}

                {(isSendingMessage || isCompassProcessing) && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="w-full"
                  >
                    <MessageLoading text={"AI is thinking"} />
                  </motion.div>
                )}
              </div>
            </div>
            <ScrollToBottomButton targetRef={lastMessageRef} containerRef={scrollContainerRef} dependency={historyData?.prev_chat} />
          </div>
        </>
      )}
      <ChatInputWithGallery
        onSendMessage={handleSendMessage} // New prop
        isSendingMessage={isSendingMessage || isCompassProcessing}
        showCompassButton={true}
        handleCompassClick={handleCompassClick}
        compassAssetSrc="../../assets/compass.png"
        requestType="discuss"
      />

      <CompassDialog
        isOpen={showCompassDialog}
        onClose={() => setShowCompassDialog(false)}
      />
    </div>
  );
};

const Discuss: React.FC<{ onNewMessage?: () => void }> = ({ onNewMessage }) => {


  const [showCompassDialog, setShowCompassDialog] = React.useState(true);

  const handleSocketUpdate = React.useCallback(() => {
    setShowCompassDialog(false);
  }, []);

  return (
    <CompassSocketProvider onUpdate={handleSocketUpdate} onMessage={onNewMessage}>
      <DiscussContent
        showCompassDialog={showCompassDialog}
        setShowCompassDialog={setShowCompassDialog}
      />
    </CompassSocketProvider>
  );
};

export default Discuss;
